/** Per-browser settings of the drawings library. */

const DEVICE_NAME_KEY = "excalidraw-library-device";
const DEVICE_ID_KEY = "excalidraw-library-device-id";
/** the device name changed and must be sent to the server */
const DEVICE_NAME_DIRTY_KEY = "excalidraw-library-device-dirty";
const CONFLICT_MODE_KEY = "excalidraw-library-conflict-mode";
/** user the local library belongs to (see sync.ts) */
export const LIBRARY_USER_KEY = "excalidraw-library-user";

/** "ask": let the user decide; "merge": merge automatically */
export type ConflictMode = "ask" | "merge";

const read = (key: string) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const write = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch (error) {
    console.error(error);
  }
};

const detectBrowser = (userAgent: string) => {
  if (/Edg\//.test(userAgent)) {
    return "Edge";
  }
  if (/Firefox\//.test(userAgent)) {
    return "Firefox";
  }
  if (/Chrome\//.test(userAgent)) {
    return "Chrome";
  }
  if (/Safari\//.test(userAgent)) {
    return "Safari";
  }
  return "Browser";
};

const detectOS = (userAgent: string) => {
  if (/iPhone/.test(userAgent)) {
    return "iPhone";
  }
  if (/iPad/.test(userAgent)) {
    return "iPad";
  }
  if (/Android/.test(userAgent)) {
    return "Android";
  }
  if (/Windows/.test(userAgent)) {
    return "Windows";
  }
  if (/Mac OS X/.test(userAgent)) {
    return "macOS";
  }
  if (/Linux/.test(userAgent)) {
    return "Linux";
  }
  return "";
};

export const defaultDeviceName = () => {
  const userAgent = navigator.userAgent;
  const os = detectOS(userAgent);
  return [detectBrowser(userAgent), os].filter(Boolean).join(" · ");
};

export const getDeviceName = () => read(DEVICE_NAME_KEY) || defaultDeviceName();

export const setDeviceName = (name: string) => {
  write(DEVICE_NAME_KEY, name.trim().slice(0, 100));
  write(DEVICE_NAME_DIRTY_KEY, "1");
};

export const isDeviceNameDirty = () => read(DEVICE_NAME_DIRTY_KEY) === "1";

export const clearDeviceNameDirty = () => write(DEVICE_NAME_DIRTY_KEY, "");

/** stable id of this browser: device names are resolved by id on the server,
 * so renaming a device updates the whole history */
export const getDeviceId = () => {
  let id = read(DEVICE_ID_KEY);
  if (!id) {
    id =
      typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    write(DEVICE_ID_KEY, id);
  }
  return id;
};

export const getLibraryUser = () => read(LIBRARY_USER_KEY);

export const setLibraryUser = (user: string) => write(LIBRARY_USER_KEY, user);

export const getConflictMode = (): ConflictMode =>
  read(CONFLICT_MODE_KEY) === "merge" ? "merge" : "ask";

export const setConflictMode = (mode: ConflictMode) =>
  write(CONFLICT_MODE_KEY, mode);
